/**
 * Manage Variables popover — anchored under the top-right "Manage Variables"
 * button in the Templates tab. Wrangle parity (images 18/19/20):
 *
 *   ┌──────────────────────────────────────┐
 *   │ location · San Francisco, CA   ✎  🗑  │
 *   ├──────────────────────────────────────┤
 *   │ Name                                 │
 *   │ [ Enter variable name            ]   │
 *   │ Value                                │
 *   │ [ Enter variable value           ]   │
 *   │ ───────────────────────────────────  │
 *   │                    Cancel | Add Var  │
 *   └──────────────────────────────────────┘
 *
 * All state lives in localStorage via `emailVariablesStore` — no network.
 */

import { useEffect, useRef, useState } from 'react';
import { Pencil, Trash2, X } from 'lucide-react';
import {
  addEmailVariable,
  deleteEmailVariable,
  listEmailVariables,
  updateEmailVariable,
  type EmailVariable,
} from '../lib/emailVariablesStore';

interface Props {
  orgId: string;
  onClose: () => void;
}

export function ManageVariablesPopover({ orgId, onClose }: Props) {
  const [vars, setVars] = useState<EmailVariable[]>(() => listEmailVariables(orgId));
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onClick = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [onClose]);

  useEffect(() => {
    nameInputRef.current?.focus();
  }, []);

  const reload = () => setVars(listEmailVariables(orgId));

  const handleAdd = () => {
    if (!name.trim()) return;
    if (editingId) {
      updateEmailVariable(orgId, editingId, { name, value });
      setEditingId(null);
    } else {
      addEmailVariable(orgId, { name, value });
    }
    setName('');
    setValue('');
    reload();
  };

  const handleEdit = (v: EmailVariable) => {
    setEditingId(v.id);
    setName(v.name);
    setValue(v.value);
    nameInputRef.current?.focus();
  };

  const handleDelete = (id: string) => {
    deleteEmailVariable(orgId, id);
    if (editingId === id) {
      setEditingId(null);
      setName('');
      setValue('');
    }
    reload();
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setName('');
    setValue('');
  };

  const canSubmit = name.trim().length > 0;

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-label="Manage email variables"
      className="absolute right-0 top-full z-[200] mt-2 w-[300px] overflow-hidden rounded-[10px] border shadow-xl"
      style={{
        background: 'var(--vscode-bg-elevated)',
        borderColor: 'var(--vscode-border-primary)',
      }}
    >
      {/* Existing variables list */}
      {vars.length > 0 && (
        <ul
          className="max-h-[180px] overflow-y-auto border-b"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
        >
          {vars.map((v) => (
            <li
              key={v.id}
              className="group flex items-center gap-2 px-3 py-2 hover:bg-[var(--vscode-bg-hover)]"
            >
              <span
                className="truncate text-[12px] font-medium"
                style={{ color: 'var(--vscode-text-primary)' }}
                title={v.name}
              >
                {v.name.toLowerCase()}
              </span>
              <span
                className="flex-1 truncate text-[11.5px]"
                style={{ color: 'var(--vscode-text-tertiary)' }}
                title={v.value}
              >
                {v.value || '—'}
              </span>
              <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                <button
                  type="button"
                  onClick={() => handleEdit(v)}
                  className="flex h-6 w-6 items-center justify-center rounded hover:bg-[var(--vscode-bg-tertiary)]"
                  style={{ color: 'var(--vscode-text-tertiary)' }}
                  title="Edit"
                  aria-label={`Edit variable ${v.name}`}
                >
                  <Pencil className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(v.id)}
                  className="flex h-6 w-6 items-center justify-center rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                  style={{ color: '#DC2626' }}
                  title="Delete"
                  aria-label={`Delete variable ${v.name}`}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Form */}
      <div className="flex flex-col gap-2.5 px-3.5 pt-3 pb-2.5">
        <div className="flex flex-col gap-1">
          <label
            className="text-[11.5px] font-medium"
            style={{ color: 'var(--vscode-text-primary)' }}
          >
            Name
          </label>
          <input
            ref={nameInputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canSubmit) {
                e.preventDefault();
                handleAdd();
              }
            }}
            placeholder="Enter variable name"
            className="h-8 rounded-[6px] border px-2.5 text-[12px] outline-none focus:ring-1 focus:ring-[#10B981]"
            style={{
              background: 'var(--vscode-bg-primary)',
              borderColor: 'var(--vscode-border-primary)',
              color: 'var(--vscode-text-primary)',
            }}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label
            className="text-[11.5px] font-medium"
            style={{ color: 'var(--vscode-text-primary)' }}
          >
            Value
          </label>
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canSubmit) {
                e.preventDefault();
                handleAdd();
              }
            }}
            placeholder="Enter variable value"
            className="h-8 rounded-[6px] border px-2.5 text-[12px] outline-none focus:ring-1 focus:ring-[#10B981]"
            style={{
              background: 'var(--vscode-bg-primary)',
              borderColor: 'var(--vscode-border-primary)',
              color: 'var(--vscode-text-primary)',
            }}
          />
        </div>
      </div>

      {/* Footer */}
      <div
        className="flex items-center justify-between gap-2 border-t px-3 py-2.5"
        style={{ borderColor: 'var(--vscode-border-primary)' }}
      >
        <button
          type="button"
          onClick={editingId ? handleCancelEdit : onClose}
          className="inline-flex items-center gap-1 rounded-[6px] px-2 py-1 text-[12px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
          style={{ color: 'var(--vscode-text-secondary)' }}
        >
          {editingId ? (
            <>
              <X className="h-3.5 w-3.5" />
              Cancel
            </>
          ) : (
            'Cancel'
          )}
        </button>
        <button
          type="button"
          onClick={handleAdd}
          disabled={!canSubmit}
          className="inline-flex items-center gap-1.5 rounded-[6px] px-3 py-1.5 text-[12px] font-medium text-white transition-opacity disabled:opacity-50"
          style={{ background: '#10B981' }}
        >
          {editingId ? 'Save' : 'Add Variable'}
        </button>
      </div>
    </div>
  );
}
